import { Divider, ListItemIcon, ListItemText } from '@mui/material'
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router'
import { Query_Root } from 'src/@types/graphql'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { ENTITY_TYPE, EXPORT_FORMAT, FILE_TYPE } from 'src/shared/constants/constants'
import { DownloadIcon } from 'src/shared/icons/Icons'
import { ExportMenu } from 'src/shared/menu/ExportMenu'
import { queryFile } from 'src/shared/presentation/fileQueries'
import { S } from 'src/shared/styled/S'
import { useUserLocale } from 'src/user/UserContext'
import { useClient } from 'urql'
import { LoadingMenuItem } from 'src/shared/menu/item/LoadingMenuItem'
import { downloadFileByUrl } from 'src/shared/utils/BlobUtils'
import { FileService } from 'src/service/axios/FileService'

interface RefHandle {
  refetch: () => void
}

export const ProgramExportMenu = forwardRef<RefHandle>((_, ref) => {
  const { getMessage } = useMessageSource()
  const { programId } = useParams()
  const language = useUserLocale()
  const urqlClient = useClient()

  const exportMenuRef = useRef<{ closeMenu: () => void }>({
    closeMenu: () => {},
  })

  const fileType = useMemo(() => FILE_TYPE.PROGRAM_APPLICATION_PDF, [])
  const entityType = useMemo(() => ENTITY_TYPE.KAP_PROGRAM, [])

  const [data, setData] = useState<{ file: Query_Root['file'] }>()
  const [loadingExportApplication, setLoadingExportApplication] = useState(false)
  const [loadingExportDescription, setLoadingExportDescription] = useState(false)

  const fetchFiles = useCallback(async () => {
    const { data } = await urqlClient
      .query<
        { file: Query_Root['file'] },
        {
          fileType: string
          entityType: string
          entityId: number
        }
      >(queryFile, { fileType, entityType, entityId: parseInt(programId as string) })
      .toPromise()

    if (data) {
      setData(data)
    }
  }, [urqlClient, fileType, entityType, programId])

  useEffect(() => {
    fetchFiles()
  }, [fetchFiles])

  useImperativeHandle(ref, () => ({
    refetch() {
      fetchFiles()
    },
  }))

  const handleProgramApplicationExport = () => {
    setLoadingExportApplication(true)

    const fileUUID = data?.file?.[0]?.uuid

    if (!fileUUID) {
      setLoadingExportApplication(false)
      return
    }

    FileService.download(fileUUID)
      .then(() => exportMenuRef.current.closeMenu())
      .finally(() => setLoadingExportApplication(false))
  }

  const handleProgramDescriptionExport = () => {
    setLoadingExportDescription(true)
    downloadFileByUrl(`api/kap/program/${programId}/export/description?lang=${language}`)
      .then(() => exportMenuRef.current.closeMenu())
      .finally(() => setLoadingExportDescription(false))
  }

  return (
    <ExportMenu ref={exportMenuRef}>
      <LoadingMenuItem
        loading={loadingExportDescription}
        disabled={loadingExportDescription}
        onClick={handleProgramDescriptionExport}
      >
        <ListItemText>{getMessage('label.export.program.description')}</ListItemText>
        <S.Typography.ExportFormat>({EXPORT_FORMAT.WORD})</S.Typography.ExportFormat>
      </LoadingMenuItem>
      <Divider />
      <LoadingMenuItem
        loading={loadingExportApplication}
        disabled={(data?.file?.length === 0 ?? true) || loadingExportApplication}
        onClick={handleProgramApplicationExport}
      >
        <ListItemIcon>
          <DownloadIcon />
        </ListItemIcon>
        <ListItemText>{getMessage('button.download.application')}</ListItemText>
        <S.Typography.ExportFormat>({EXPORT_FORMAT.PDF})</S.Typography.ExportFormat>
      </LoadingMenuItem>
    </ExportMenu>
  )
})
